
import { computed, defineComponent, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'

import columnsIcon from '@/assets/icons/columns.svg'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import Filters from '@/modules/CmsModule/components/Filters/index.vue'
import Invoices from '@/modules/CmsModule/components/Invoices/index.vue'

import cmsAPI from '@/new-services/cms.service'

import { CmsInvoices, GetInvoicesParams } from '@/new-services/types/cms'
import { ColumnItemT } from '@/core/types/tables.types'

import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'

const COLUMNS = [
    { id: 1, name: 'Созданные', key: 'created' },
    { id: 2, name: 'На заборе', key: 'onTake' },
    { id: 5, name: 'На обработке', key: 'inProcessing' },
    { id: 6, name: 'В накопителе', key: 'inWarehouse' },
    { id: 7, name: 'На транзите', key: 'inTransit' },
    { id: 8, name: 'Город назначения', key: 'destinationCity' },
    { id: 9, name: 'На доставке', key: 'onDelivery' },
    { id: 10, name: 'Доставлено', key: 'delivered' },
]

const validKeys: Array<keyof CmsInvoices> = [
    'created',
    'delivered',
    'destinationCity',
    'inProcessing',
    'inTransit',
    'inWarehouse',
    'onDelivery',
    'onTake',
]

export default defineComponent({
    components: {
        Filters,
        Invoices,
        NewButton,
        NewCheckbox,
        ButtonDropdown,
        TheButton,
        columnsIcon,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()
        const { path } = useRoute()
        const tablesStore = useTablesStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_open: false,
            is_excel_loading: false,
            is_kaspi_report_loading: false,
            items: null as CmsInvoices | null,
            columns: [] as ColumnItemT[],
        })

        const filters = reactive({
            invoiceNumber: null as string | null,
            orderNumber: null as string | null,
            notCompanyIds: [] as number[],
            companyIds: [] as number[],
            shipmentTypeId: null as number | null,
            needCashSum: null as number | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            managerIds: [] as number[],
            isSelfDelivery: null as number | null,
            isPickup: null as number | null,
            hasWaitListStatuses: 0,
            isDelaying: 0,
        })

        const loadInvoices = async () => {
            try {
                state.is_failed = false
                state.is_loading = true

                const params = { ...filters }
                const response = await cmsAPI.getInvoices(params)
                state.items = { ...response.data.data }
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const onApplyFilters = (params: GetInvoicesParams) => {
            filters.invoiceNumber = params.invoiceNumber
            filters.orderNumber = params.orderNumber
            filters.notCompanyIds = params.notCompanyIds
            filters.companyIds = params.companyIds
            filters.shipmentTypeId = params.shipmentTypeId
            filters.needCashSum = params.needCashSum
            filters.fromCityId = params.fromCityId
            filters.toCityId = params.toCityId
            filters.managerIds = params.managerIds
            filters.isPickup = params.isPickup
            filters.isSelfDelivery = params.isSelfDelivery
            filters.hasWaitListStatuses = params.hasWaitListStatuses
            filters.isDelaying = params.isDelaying

            loadInvoices()
        }

        const onResetFilters = () => {
            filters.invoiceNumber = null
            filters.orderNumber = null
            filters.notCompanyIds = []
            filters.companyIds = []
            filters.shipmentTypeId = null
            filters.needCashSum = null
            filters.fromCityId = null
            filters.toCityId = null
            filters.managerIds = []
            filters.isPickup = null
            filters.isSelfDelivery = null
            filters.isDelaying = 0
            filters.hasWaitListStatuses = 0

            loadInvoices()
        }

        const isAllColumnsChecked = computed(() =>
            state.columns.every((column) => column.isChecked)
        )

        const getStatusIds = () => {
            let statusIds: number[] | null = null

            if (!isAllColumnsChecked.value) {
                statusIds = []
                state.columns.forEach((column) => {
                    if (column.isChecked && statusIds) {
                        statusIds.push(column.id as number)
                    }
                })
                if (statusIds.includes(2)) {
                    statusIds.push(3)
                }
            }

            return statusIds
        }

        const getFormattedNowDate = () => {
            const today = new Date()
            const yyyy = today.getFullYear()
            let month: string | number = today.getMonth() + 1
            let day: string | number = today.getDate()

            if (day < 10) day = '0' + day
            if (month < 10) month = '0' + month

            const formattedToday = day + '.' + month + '.' + yyyy
            return formattedToday
        }

        const downloadReport = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    ...filters,
                    statusIds: getStatusIds(),
                }

                const response = await cmsAPI.downloadExcel(params)
                saveFile(response.data, `Накладные на сегодня (${getFormattedNowDate()}).xlsx`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const downloadKaspiReport = async () => {
            try {
                state.is_kaspi_report_loading = true

                const params = {
                    managerIds: filters.managerIds,
                }

                const response = await cmsAPI.downloadKaspiReport(params)
                saveFile(response.data, `Отчет каспи от ${getFormattedNowDate()}.xlsx`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При выгрузке отчета Kaspi произошла ошибка',
                })
            } finally {
                state.is_kaspi_report_loading = false
            }
        }

        const getColumns = () => (state.columns = tablesStore.getColumns(path, COLUMNS))
        const setColumns = () => tablesStore.setColumns(path, state.columns)

        const toggle = () => {
            if (state.is_open) setColumns()
            state.is_open = !state.is_open
        }

        const onAllColumnsSelect = (value: boolean) => {
            if (value) {
                state.columns.forEach((column) => {
                    column.isChecked = true
                })
            } else {
                state.columns.forEach((column) => {
                    column.isChecked = false
                })
            }
        }

        const onSetColumns = () => {
            setColumns()
            toggle()
        }

        const filteredColumns = computed(() => state.columns.filter((c) => c.isChecked))

        onMounted(() => {
            getColumns()
        })

        return {
            state,
            filters,
            onApplyFilters,
            onResetFilters,
            downloadReport,
            downloadKaspiReport,
            onSetColumns,
            toggle,
            onAllColumnsSelect,
            isAllColumnsChecked,
            filteredColumns,
            loadInvoices,
        }
    },
})
